/**
 * The car photo uploader (§7).
 *
 * The sequence matters and is not the obvious one:
 *
 *   1. **Resize first, ask second.** The API refuses to mint a policy for a
 *      declared length over the cap (`createImageUploadRequest`), so asking
 *      before resizing just earns a 422. More to the point, the presigned POST
 *      has a short life — spending it while a 6 MB photo re-encodes is how you
 *      get an expired policy.
 *   2. **POST to S3 directly**, with the returned fields *before* the file. S3
 *      streams the form and stops at the file part, so any field after it is
 *      never seen (see uploadToS3).
 *   3. **The key is already attached** to the car server-side, before the bytes
 *      land (routes/fleet.ts). So a failed upload leaves the car pointing at a
 *      key that never materialised — which the CDN serves as a 404 and this
 *      component renders as "no photo". That is the intended worst case.
 *
 * Owner-only, mirroring the route.
 */

import { useRef, useState } from 'react';
import { Camera, Car, TriangleAlert } from 'lucide-react';

import { IMAGE_MAX_BYTES } from '@svcdue/contracts';
import type { CarDto } from '@svcdue/contracts';
import { useQueryClient } from '@tanstack/react-query';

import { createImageUpload } from '../api/endpoints';
import { keys } from '../api/keys';
import { errorMessage } from '../components/states';
import { ImageTooLargeError, prepareCarPhoto, uploadToS3 } from '../lib/image';
import { usePermissions } from './GarageProvider';

/**
 * Anything a canvas can decode. Deliberately wider than the API's accepted
 * *output* types (`IMAGE_CONTENT_TYPES`): a HEIC from an iPhone or a 12 MP PNG
 * is fine as an input — it gets re-encoded to JPEG or WebP on the way out — so
 * refusing it at the file picker would reject photos we can actually handle.
 */
const ACCEPT = 'image/*';

export function CarPhoto({ garageId, car }: { garageId: string; car: CarDto }) {
  const { canManageFleet } = usePermissions();
  const input = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** A local preview, so the photo appears before the CDN has it. */
  const [preview, setPreview] = useState<string | null>(null);

  async function onPick(file: File) {
    setError(null);
    try {
      setBusy('Resizing…');
      const resized = await prepareCarPhoto(file);

      setBusy('Preparing upload…');
      const upload = await createImageUpload(garageId, car.id, {
        contentType: resized.type as 'image/jpeg' | 'image/webp' | 'image/png',
        contentLength: resized.blob.size,
      });

      setBusy('Uploading…');
      await uploadToS3(upload.url, upload.fields, resized.blob);

      /**
       * Show the bytes we just sent rather than waiting on the CDN. The car row
       * already carries the key, but CloudFront may not have the object for a
       * moment, and a broken image where a photo should be reads as a failure.
       */
      setPreview(URL.createObjectURL(resized.blob));
      void queryClient.invalidateQueries({ queryKey: keys.garage(garageId) });
    } catch (err) {
      // The cap is the one failure the user can actually act on, so it gets its
      // own message from the resizer rather than a generic upload error.
      setError(err instanceof ImageTooLargeError ? err.message : errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  const src = preview ?? car.imageUrl;

  return (
    <div className="carphoto">
      <div className="carphoto-frame">
        {src ? <img src={src} alt={car.nickname} /> : <Car size={28} />}
        {busy && <div className="carphoto-busy">{busy}</div>}
      </div>

      {canManageFleet && (
        <>
          <input
            ref={input}
            className="carphoto-input"
            type="file"
            accept={ACCEPT}
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Reset the input so picking the *same* file again still fires a
              // change event — otherwise a retry after a failure does nothing.
              e.target.value = '';
              if (file) void onPick(file);
            }}
          />
          <button className="act act-sm" onClick={() => input.current?.click()} disabled={busy !== null}>
            <Camera size={11} /> {src ? 'Replace photo' : 'Add photo'}
          </button>
          <span className="carphoto-hint">
            Resized in your browser to stay under {Math.floor(IMAGE_MAX_BYTES / 1024)} KB.
          </span>
        </>
      )}

      {error && (
        <div className="field-err carphoto-err">
          <TriangleAlert size={12} /> {error}
        </div>
      )}
    </div>
  );
}
